import { ComponentProps, useEffect, useMemo, useRef, useState } from 'react'
import { MyCollectionViewProps } from './MyCollection.types'
import { GalleryType, HeadlessGallery } from 'core/logic/gallery/gallery.types'
import { AssetWithBalance, OwnOfferAsset } from 'core/logic/asset/asset.types'
import {
  BaseAssetMargin,
  useGalleryBaseWidth,
  GalleryThumbnail,
} from 'openspace/components/GalleryCard/GalleryCard.view'
import { i18n, tO } from 'translate/i18n'
import CollectionItem from 'pages/Authenticated/MyCollection/CollectionItem'
import PaginableContainer from 'openspace/components/PaginableContainer'
import { useLocalPagination } from '@onepercentio/one-ui/hooks/usePagination'
import useElementFit from '@onepercentio/one-ui/hooks/useElementFit'
import { SectionView } from 'openspace/components/Section/Section'
import Button from '@onepercentio/one-ui/components/Button'
import AdaptiveContainer from '@onepercentio/one-ui/components/AdaptiveContainer'
import Container from 'openspace/components/Container'
import useLocalStorage from '@onepercentio/one-ui/hooks/persistence/useLocalStorage'
import EmptyCollection from 'pages/Authenticated/MyCollection/EmptyCollection'
import { MyCollectionTestIds } from './MyCollection.e2e'
import { DeliveryType } from 'core/logic/delivery/delivery.types'
import { PendingPurchase } from 'core/logic/purchase/purchase.types'
import SectionLoader from 'openspace/components/SectionLoader/SectionLoader'
import FooterLogic from 'components/Footer/Footer.logic'
import Styles from './MyCollection.module.scss'
import ContainerListPadding from 'components/ContainerListPadding'
import {
  AssetType,
  Sale,
} from 'core/logic/asset/asset.types.ts'
import { TrackingStatusLabel } from '../Tracking/Tracking.view.tsx'
import { TrackingStatus } from '../Tracking/Tracking.types.ts'
import useHero from '@onepercentio/one-ui/hooks/useHero'
import AssetDisplayLogic from 'openspace/components/AssetDisplay/AssetDisplay.logic.tsx'
import PingPongText from '@onepercentio/one-ui/components/PingPongText'
import OfferPriceLogic from 'components/offer/OfferPrice/OfferPrice.logic.tsx'
import Text from 'openspace/components/Text/index.tsx'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import {
  FigmaTypo,
  LPTypo,
  Typo,
} from 'containers/OneUIProvider/OneUIProvider.data.tsx'

/**
 * The list of owned collections
 **/
export default function MyCollectionView(
  props: ComponentProps<typeof MyCollectionViewBase>
) {
  return (
    <>
      <MyCollectionViewBase {...props} />
      <FooterLogic />
    </>
  )
}

export function MyCollectionViewBase({
  galleries,
  ...props
}: MyCollectionViewProps) {
  const { galleryIds, assetByGallery: assetsByGallery } = useMemo(() => {
    const galleryMap = [
      ...(props.closedCollection || []),
      ...(props.pending || []),
      ...(props.openedCollection || []),
    ].reduce(
      (r, assetOrPurchaseOrDelivery) => {
        function returnGalleryMapWithAsset(galleryId?: string) {
          return {
            ...r,
            [galleryId! || 'default']: [
              ...(r[galleryId! || 'default'] || []),
              assetOrPurchaseOrDelivery,
            ],
          }
        }
        if ('sellOrder' in assetOrPurchaseOrDelivery)
          return returnGalleryMapWithAsset(
            assetOrPurchaseOrDelivery.sellOrder.galleryId
          )
        if ('type' in assetOrPurchaseOrDelivery) {
          if (assetOrPurchaseOrDelivery.type === 'airdrop') {
            return returnGalleryMapWithAsset(
              (assetOrPurchaseOrDelivery as AssetWithBalance).asset!.galleryId
            )
          }
        }
        const galleryId =
          'galleryId' in assetOrPurchaseOrDelivery
            ? assetOrPurchaseOrDelivery.galleryId
            : 'nextAction' in assetOrPurchaseOrDelivery
              ? assetOrPurchaseOrDelivery.nextAction!.data.galleryId
              : assetOrPurchaseOrDelivery.offer!.galleryId
        return returnGalleryMapWithAsset(galleryId)
      },
      {} as {
        [g: GalleryType['id']]: (
          | DeliveryType
          | PendingPurchase
          | AssetWithBalance
          | OwnOfferAsset
        )[]
      }
    )

    return {
      galleryIds: Object.keys(galleryMap).filter((a) => a !== 'default'),
      assetByGallery: galleryMap,
    }
  }, [props.openedCollection, props.pending, props.closedCollection])

  const defaultCollection = assetsByGallery.default || []

  const onlyExistingGalleriesIds = useMemo(
    () => galleryIds.filter((id) => galleries?.find((g) => g.id === id)),
    [galleries, galleryIds]
  )
  const isLoading =
    (galleries || undefined) === undefined ||
    (props.openedCollection || undefined) === undefined

  const gallerySections = useMemo(() => {
    const galleriesBoundSections = onlyExistingGalleriesIds.map((galleryId) => {
      const gallery = galleries!.find((g) => g.id === galleryId)!
      return (
        <GalleryWrapper
          key={gallery.id}
          gallery={gallery}
          onClickAsset={props.onClickAsset}
          opened={assetsByGallery[gallery.id]}
          isSingleGallery={galleries!.length === 1}
        />
      )
    })

    return galleriesBoundSections
  }, [assetsByGallery, defaultCollection, galleries, onlyExistingGalleriesIds])

  return (
    <>
      {isLoading ? (
        <SectionLoader data-testid={MyCollectionTestIds.LOADER} />
      ) : !onlyExistingGalleriesIds.length && !defaultCollection.length ? (
        <Container className={Styles.root}>
          <EmptyCollection data-testid={MyCollectionTestIds.EMPTY} />
        </Container>
      ) : (
        <div
          className={Styles.root}
          data-testid={MyCollectionTestIds.ASSETS}>
          {gallerySections}
        </div>
      )}
    </>
  )
}

function GalleryWrapper({
  gallery,
  opened,
  onClickAsset,
  isSingleGallery,
}: {
  gallery?: GalleryType | HeadlessGallery
  opened: (AssetWithBalance | DeliveryType | PendingPurchase | OwnOfferAsset)[]
  onClickAsset: (
    a: AssetWithBalance | DeliveryType | PendingPurchase | OwnOfferAsset
  ) => void
  isSingleGallery: boolean
}) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const isSectionCollapsable =
    isSingleGallery !== true && gallery !== undefined && 'icon' in gallery
  const [showAll, setShowAll] = useLocalStorage(
    `my-col-${gallery?.id || 'default'}-state`,
    isSectionCollapsable ? false : true
  )
  const [tempHide, setTempHide] = useState(false)
  const expanded = showAll && !tempHide

  const AssetsListInstance = useMemo(
    () =>
      !expanded
        ? AssetsListFactory(onClickAsset)
        : ({
          assets,
        }: {
          assets: (
            | AssetWithBalance
            | DeliveryType
            | PendingPurchase
            | OwnOfferAsset
          )[]
        }) => <AssetsList assets={assets} onClickAsset={onClickAsset} />,
    [onClickAsset, expanded]
  )

  const salt = useMemo(() => Date.now(), [expanded])

  return (
    <>
      <SectionView
        disableHover
        disableOrdering={isSectionCollapsable === false}
        title={
          gallery && 'icon' in gallery ? (
            <>
              <GalleryThumbnail
                gallery={gallery}
                className={Styles.thumbnail}
              />
              {gallery[i18n.language as LanguageCodes].title}
            </>
          ) : (
            <></>
          )
        }
        className={Styles.sectionRoot}>
        <Container ref={sectionRef} className={Styles.container}>
          <>
            {opened.length && (
              <AdaptiveContainer
                className={Styles.collectionContainer}
                direction='v'>
                <AssetsListInstance key={salt} assets={opened} />
              </AdaptiveContainer>
            )}

            {isSectionCollapsable && (
              <Button
                variant='transparent'
                onClick={() => {
                  if (showAll) {
                    const scrollable =
                      document.querySelector('#scrollable')!.firstElementChild!
                    scrollable.scrollTo({
                      top: sectionRef.current!.parentElement!.parentElement!
                        .parentElement!.parentElement!.parentElement!.offsetTop,
                      behavior: 'smooth',
                    })
                    setTimeout(() => {
                      setShowAll(false)
                    }, 500)
                  } else {
                    setShowAll(true)
                  }
                }}>
                {tO(
                  expanded
                    ? 'myCollection.actions.hideAllGallery'
                    : 'myCollection.actions.seeAllGallery'
                )}
              </Button>
            )}
          </>
        </Container>
      </SectionView>
    </>
  )
}

function AssetsList({
  assets,
  onClickAsset,
}: {
  assets: (AssetWithBalance | DeliveryType | PendingPurchase | OwnOfferAsset)[]
  onClickAsset: (
    asset: AssetWithBalance | DeliveryType | PendingPurchase | OwnOfferAsset
  ) => void
}) {
  const baseWidth = useGalleryBaseWidth(undefined)
  const assetWidth = useMemo(() => baseWidth + BaseAssetMargin, [baseWidth])
  const { ref, howManyItemsByRow } = useElementFit(assetWidth)
  const items = assets.map((asset) => {
    return (
      <div
        key={`a-${asset.id} t-${'tokenId' in asset ? asset.tokenId : ''}`}
        className={Styles.collectionItem}>
        <CollectionItem
          showStickyTop={false}
          assetOrDeliveryOrPurchase={asset}
          onClick={() => onClickAsset(asset)}
          data-testid={
            'purchaseId' in asset
              ? MyCollectionTestIds.PENDING
              : 'offerResume' in asset && asset.offerResume
                ? MyCollectionTestIds.ITEM_ON_SALE
                : MyCollectionTestIds.COLLECTION_ITEM
          }
        />
      </div>
    )
  })
  return (
    <div className={Styles.fullList} ref={ref}>
      {items}
      <ContainerListPadding
        howManyItemsShowing={items.length}
        howManyItemsByRow={howManyItemsByRow}
        widthBoundClass={Styles.collectionItem}
      />
    </div>
  )
}

function AssetsListFactory(
  onClickAsset: (
    asset: AssetWithBalance | DeliveryType | PendingPurchase | OwnOfferAsset
  ) => void
) {
  return function AssetsList({
    assets,
  }: {
    assets: (
      | AssetWithBalance
      | DeliveryType
      | PendingPurchase
      | OwnOfferAsset
    )[]
  }) {
    const baseWidth = useGalleryBaseWidth(undefined)

    const assetWidth = useMemo(() => baseWidth + BaseAssetMargin, [baseWidth])
    const { ref, anItemMore } = useElementFit(assetWidth)
    const paginable = useLocalPagination(assets)

    useEffect(() => {
      if (anItemMore) paginable.getPage(0, anItemMore)
    }, [anItemMore, assets])

    return (
      <PaginableContainer
        ref={ref}
        src={paginable}
        jsx={{
          width: assetWidth,
          factory: (
            asset:
              | AssetWithBalance
              | DeliveryType
              | PendingPurchase
              | OwnOfferAsset
          ) => (
            <div
              className={Styles.collectionItem}
              key={`a-${asset.id} t-${'tokenId' in asset ? asset.tokenId : ''
                }`}>
              <CollectionItem
                showStickyTop={false}
                assetOrDeliveryOrPurchase={asset}
                onClick={() => onClickAsset(asset)}
                data-testid={
                  'offerResume' in asset && asset.offerResume
                    ? MyCollectionTestIds.ITEM_ON_SALE
                    : MyCollectionTestIds.COLLECTION_ITEM
                }
              />
            </div>
          ),
        }}
      />
    )
  }
}

const TAB_OPTIONS = ['items', 'offers', 'sales'] as const
export type MY_COLLECTION_SUB_MENUS = (typeof TAB_OPTIONS)[number]

export function SoldAssetItem({
  sale,
  asset,
  delivery,
  recentStatus,
  mode,
  ...props
}: {
  sale: Sale
  asset: AssetType
  delivery: DeliveryType
  recentStatus: TrackingStatus
  mode: 'buyer' | 'seller'
} & Partial<{ onOpenDelivery: (a: AssetType, d: DeliveryType) => void }>) {
  const { heroRef } = useHero(sale.offerId)
  return (
    <div
      className={`${Styles.sale}`}
      data-testid={MyCollectionTestIds.SOLD}
      key={`${sale.offerId}`}
      onClick={() => props.onOpenDelivery?.(asset, delivery)}
      ref={heroRef as any}>
      <div>
        <AssetDisplayLogic asset={asset} uniqueId={`sale-${sale.offerId}`} />
      </div>
      <div className={Styles.content}>
        <PingPongText type={LPTypo.P2}>
          {asset[i18n.language].name}
        </PingPongText>
        <Spacing size='8' />
        {mode === 'seller' ? (
          <>
            <Text code='pages.collections.sold.value' type={Typo.small} />
            <Text type={LPTypo.H6}>
              {
                <OfferPriceLogic
                  prices={{
                    [sale.currency]: sale.price,
                  }}
                  mode='text'
                  preferredCurrency={sale.currency as any}
                />
              }
            </Text>
          </>
        ) : (
          <>
            <Text code='tracking.total' type={Typo.small} />
            <Text type={LPTypo.H6}>
              {
                <OfferPriceLogic
                  prices={{
                    [sale.currency]:
                      sale.price + delivery.logistics!.quotation!.price,
                  }}
                  mode='text'
                  preferredCurrency={sale.currency as any}
                />
              }
            </Text>
          </>
        )}
        <Spacing size='16' />
        <Text code='pages.collections.sold.situation' type={Typo.small} />
        <TrackingStatusLabel status={recentStatus} />
      </div>
    </div>
  )
}
